// CountrySelector.tsx
import React, { useEffect, useRef, useState } from 'react';
import { countries as countriesData } from '@hz/stealthy-gmc-cloaker-common';
import {
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  Stack,
  Button,
  Text,
  Wrap,
  WrapItem,
  IconButton,
} from '@chakra-ui/react';
import { Search, X } from 'lucide-react';

const countries = countriesData.countries;

interface CountrySelectorProps {
  selectedCountries: string[]
  onChange: (countries: string[]) => void
}

export const CountrySelector = ({
  selectedCountries,
  onChange,
}: CountrySelectorProps) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  const searchRef = useRef<HTMLDivElement>(null)
  const filteredCountries = countries.filter(
    (country) =>
      country.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
      !selectedCountries.includes(country.code),
  )
  const handleCountrySelect = (countryCode: string) => {
    if (!selectedCountries.includes(countryCode)) {
      onChange([...selectedCountries, countryCode])
      setSearchQuery('')
      setShowDropdown(false)
    }
  }
  const handleCountryRemove = (countryCode: string) => {
    onChange(selectedCountries.filter((code) => code !== countryCode))
  }
  const getCountryFlag = (countryCode: string) => {
    return `https://flagcdn.com/24x18/${countryCode.toLowerCase()}.png`
  }
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])
  return (
    <Box position="relative" ref={searchRef}>
      <InputGroup>
        <InputLeftElement pointerEvents="none">
          <Search size={18} color="gray" />
        </InputLeftElement>
        <Input
          placeholder={
            selectedCountries.length === 0
              ? 'Select countries...'
              : 'Add more countries...'
          }
          bg="gray.700"
          border="none"
          color="white"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value)
            setShowDropdown(true)
          }}
          onFocus={() => setShowDropdown(true)}
        />
      </InputGroup>
      {showDropdown && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          right={0}
          mt={2}
          bg="gray.800"
          borderRadius="md"
          border="1px"
          borderColor="gray.700"
          boxShadow="xl"
          maxH="200px"
          overflowY="auto"
          zIndex={1000}
        >
          <Stack spacing={0}>
            {filteredCountries.map((country) => (
              <Button
                key={country.code}
                size="sm"
                variant="ghost"
                justifyContent="flex-start"
                w="full"
                color="white"
                leftIcon={
                  <Box
                    as="img"
                    src={getCountryFlag(country.code)}
                    alt={country.name}
                    w="24px"
                    h="18px"
                  />
                }
                onClick={() => handleCountrySelect(country.code)}
                _hover={{
                  bg: 'gray.700',
                }}
              >
                {country.name}
              </Button>
            ))}
            {filteredCountries.length === 0 && (
              <Text color="gray.400" p={2} fontSize="sm">
                No countries found
              </Text>
            )}
          </Stack>
        </Box>
      )}
      {selectedCountries.length > 0 && (
        <Wrap spacing={2} mt={2}>
          {selectedCountries.map((code) => {
            const country = countries.find((c) => c.code === code)
            if (!country) return null
            return (
              <WrapItem key={code}>
                <Box
                  bg="gray.700"
                  px={2}
                  py={1}
                  borderRadius="md"
                  display="flex"
                  alignItems="center"
                  gap={2}
                >
                  <Box
                    as="img"
                    src={getCountryFlag(code)}
                    alt={country.name}
                    w="24px"
                    h="18px"
                  />
                  <Text color="white" fontSize="sm">
                    {country.name}
                  </Text>
                  <IconButton
                    aria-label="Remove country"
                    icon={<X size={14} />}
                    size="xs"
                    variant="ghost"
                    color="gray.400"
                    _hover={{
                      color: 'white',
                      bg: 'gray.600',
                    }}
                    onClick={() => handleCountryRemove(code)}
                  />
                </Box>
              </WrapItem>
            )
          })}
        </Wrap>
      )}
    </Box>
  )
}
